import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Moon, Sun, Code2, Settings, Menu, X, Trophy, User, RefreshCw, Crown, PenSquare, Mail, BookOpen, Info, Users, Bell, Radio, MonitorPlay, ChevronDown, LogOut } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import AuthModal from "./AuthModal";
import ContactModal from "./ContactModal";
import { ModeToggle } from "./ModeToggle";
import { useMode } from "../context/ModeContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// Use empty string for same-origin API calls (works on both preview and production)
const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

export default function Navbar({ darkMode, setDarkMode, user, setUser, refreshKey }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [canRegenerate, setCanRegenerate] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const { mode } = useMode();

  useEffect(() => {
    if (user) {
      fetchStats();
      checkCanRegenerate();
      fetchNotifications();
    }
  }, [user, refreshKey, mode]);

  // Listen for custom event to refresh stats (triggered from drill completions, etc.)
  useEffect(() => {
    const handleRefreshStats = () => {
      if (user) {
        fetchStats();
      }
    };
    
    window.addEventListener('refreshUserStats', handleRefreshStats);
    return () => window.removeEventListener('refreshUserStats', handleRefreshStats);
  }, [user]);

  // Refresh notifications periodically
  useEffect(() => {
    if (user) {
      const interval = setInterval(fetchNotifications, 60000); // Every minute
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/user/stats?mode=${mode}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${API}/user/notifications`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setNotificationCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Dismiss the notification
    try {
      const response = await fetch(`${API}/user/notifications/${notification.id}/dismiss`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Show toast for points awarded
        if (data.points_awarded && data.points_awarded > 0) {
          toast.success(`+${data.points_awarded} points ${data.bonus_type}! 🎉`, {
            description: "Your question is now live for other students to solve!",
            duration: 5000,
          });
          
          // Refresh stats to update points in navbar
          fetchStats();
        }
      }
    } catch (error) {
      console.error("Error dismissing notification:", error);
    }

    // Navigate if there's an action URL
    if (notification.action_url) {
      navigate(notification.action_url);
    }

    // Refresh notifications
    fetchNotifications();
  };

  const checkCanRegenerate = async () => {
    try {
      const response = await fetch(`${API}/auth/can-regenerate-nickname`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setCanRegenerate(data.can_regenerate);
      }
    } catch (error) {
      console.error("Error checking regenerate status:", error);
    }
  };

  const handleRegenerateNickname = async () => {
    if (!canRegenerate) {
      toast.error("You can only regenerate your nickname once per day");
      return;
    }
    
    setRegenerating(true);
    try {
      const response = await fetch(`${API}/auth/regenerate-nickname`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update user with new nickname
        setUser(prev => ({ ...prev, nickname: data.nickname }));
        setCanRegenerate(false);
        setShowRegenerateModal(false);
        toast.success(`New nickname: ${data.nickname}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to regenerate nickname");
      }
    } catch (error) {
      toast.error("Error regenerating nickname");
    } finally {
      setRegenerating(false);
    }
  };

  const handleLogin = () => {
    setShowAuthModal(true);
  };

  const handleAuthSuccess = async (userData) => {
    // Read session data from sessionStorage (for anonymous users who completed questions)
    const getSessionData = () => {
      try {
        const data = sessionStorage.getItem('dailyCodingSession');
        if (data) {
          const parsed = JSON.parse(data);
          const today = new Date().toISOString().split('T')[0];
          if (parsed.date === today) {
            return parsed;
          }
        }
      } catch (e) {
        console.error('Error reading session data:', e);
      }
      return {
        date: new Date().toISOString().split('T')[0],
        answeredQuestions: [],
        sessionScore: 0,
        dailyQuestionsCompleted: 0,
        challengesCompleted: 0
      };
    };
    
    const sessionData = getSessionData();
    console.log("Navbar handleAuthSuccess - Session data:", sessionData);
    
    // Merge session data with new account if there's any progress
    if (sessionData.answeredQuestions.length > 0 || sessionData.sessionScore > 0 || sessionData.dailyQuestionsCompleted > 0) {
      try {
        console.log("Navbar - Calling merge-session...");
        const response = await fetch(`${API}/user/merge-session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            answered_questions: sessionData.answeredQuestions || [],
            session_score: sessionData.sessionScore || 0,
            daily_questions_completed: sessionData.dailyQuestionsCompleted || 0,
            challenges_completed: sessionData.challengesCompleted || 0
          })
        });
        const result = await response.json();
        console.log("Navbar - Merge result:", result);
        
        // Store merged state in localStorage so DailyChallenge knows about it
        const DAILY_QUESTIONS_REQUIRED = 3;
        localStorage.setItem('mergedAuthState', JSON.stringify({
          warmUpProgress: sessionData.dailyQuestionsCompleted || 0,
          challengesCompleted: sessionData.challengesCompleted || 0,
          startInChallengeMode: sessionData.dailyQuestionsCompleted >= DAILY_QUESTIONS_REQUIRED,
          timestamp: Date.now()
        }));
        
        // Clear session storage after merge
        sessionStorage.removeItem('dailyCodingSession');
        
        if (result.points_added > 0) {
          toast.success(`Added ${result.points_added} points from your session!`);
        }
      } catch (error) {
        console.error("Error merging session:", error);
      }
    }
    
    setUser(userData);
    
    // Reload page to get fresh state
    window.location.reload();
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      
      // Clear all session data
      localStorage.removeItem('dailyWarmUpCompleted');
      localStorage.removeItem('mergedAuthState');
      localStorage.removeItem('mergedStateAppliedFlag');
      localStorage.removeItem('dailyCodingSession');
      sessionStorage.removeItem('dailyCodingSession');
      sessionStorage.clear();
      
      setUser(null);
      setStats(null);
      
      // Redirect to home page to reset to warm-up mode
      window.location.href = '/';
    } catch (error) {
      console.error("Logout error:", error);
      // Still clear local data even if API fails
      localStorage.removeItem('dailyWarmUpCompleted');
      localStorage.removeItem('mergedAuthState');
      localStorage.removeItem('mergedStateAppliedFlag');
      sessionStorage.clear();
      setUser(null);
      setStats(null);
      window.location.href = '/';
    }
  };

  // Link labels are always visible (icon-only links were unclear). The full bar is measured against the
  // available width and the whole navigation moves into the menu button only when it genuinely doesn't fit -
  // so an admin on a 1440px laptop still gets the full bar while a school admin with extra links may not.
  const navLabel = "inline";
  const rowRef = useRef(null);
  const logoRef = useRef(null);
  const linksRef = useRef(null);
  const utilsRef = useRef(null);
  const [collapsed, setCollapsed] = useState(false);
  useLayoutEffect(() => {
    const measure = () => {
      const row = rowRef.current;
      if (!row) return;
      const needed = (logoRef.current?.offsetWidth || 0) + (linksRef.current?.scrollWidth || 0) + (utilsRef.current?.scrollWidth || 0) + 20 + 24 + 16;
      setCollapsed(needed > row.clientWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    [rowRef, linksRef, utilsRef].forEach((r) => r.current && ro.observe(r.current));
    return () => ro.disconnect();
  }, []);
  // Collapsed: keep the full bar in the DOM (invisible, out of flow, not focusable) so it can still be measured
  const desktopCls = (base) => (collapsed ? `${base} invisible absolute left-0 top-0 pointer-events-none` : base);

  return (
    <nav className="sticky top-0 z-50 glass-nav overflow-x-clip" data-testid="navbar">
      <div className="max-w-7xl xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={rowRef} className="relative flex items-center justify-between h-16 gap-6" data-nav-collapsed={collapsed ? "true" : "false"}>
          {/* Left: Logo + primary navigation */}
          <div className="flex items-center gap-5 min-w-0">
            {/* Logo */}
            <Link 
              ref={logoRef}
              to="/" 
              className="flex items-center gap-2 group shrink-0"
              data-testid="nav-logo"
            >
              <div className="p-2 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors">
                <Code2 className="w-6 h-6 text-primary" />
              </div>
              <span className="font-heading font-bold text-lg hidden sm:block">
                <span className="text-primary">CodingDrills</span><span className="text-foreground">.au</span>
              </span>
            </Link>

            {/* Desktop Primary Navigation */}
            <div ref={linksRef} className={desktopCls("flex items-center gap-2 whitespace-nowrap")} aria-hidden={collapsed} data-testid="desktop-nav-links">
            {user?.is_admin && (
              <Link
                to="/admin"
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                  location.pathname.startsWith("/admin")
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="nav-admin"
              title="Admin"
              >
                <Settings className="w-4 h-4" />
                <span className={navLabel}>Admin</span>
              </Link>
            )}

            {/* Manage Students Link - School Admins Only */}
            {user?.school_admin && (
              <Link
                to="/manage-students"
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                  location.pathname === "/manage-students"
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="nav-manage-students"
                title="Students"
              >
                <Users className="w-4 h-4" />
                <span className={navLabel}>Students</span>
              </Link>
            )}

            {/* Host Live Drill - School Admins Only */}
            {user?.school_admin && (
              <Link
                to="/live-host"
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                  location.pathname === "/live-host"
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="nav-host-live"
              title="Host Live"
              >
                <MonitorPlay className="w-4 h-4" />
                <span className={navLabel}>Host Live</span>
              </Link>
            )}

            {/* Join Live Drill - Everyone (incl. school admins joining another teacher's drill) */}
            <Link
              to="/join"
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === "/join"
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="nav-join-live"
              title="Join Live"
            >
              <Radio className="w-4 h-4" />
              <span className={navLabel}>Join Live</span>
            </Link>

            {/* Rankings Link */}
            <Link
              to="/rankings"
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                location.pathname === "/rankings"
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="nav-rankings"
              title="Rankings"
            >
              <Trophy className="w-4 h-4" />
              <span className={navLabel}>Rankings</span>
            </Link>

            {/* Instructions Link */}
            <Link
              to="/instructions"
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                location.pathname === "/instructions"
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="nav-instructions"
              title="Instructions"
            >
              <BookOpen className="w-4 h-4" />
              <span className={navLabel}>Instructions</span>
            </Link>

            {/* Create Question - for all logged-in users */}
            {user && (
              <Link
                to="/create-question"
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                  location.pathname === "/create-question"
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="nav-create-question"
              title="Create"
              >
                <PenSquare className="w-4 h-4" />
                <span className={navLabel}>Create</span>
              </Link>
            )}
            </div>
          </div>

          {/* Right: utility items (score, theme, notifications, auth) */}
          <div ref={utilsRef} className={desktopCls("flex items-center gap-2.5 shrink-0 whitespace-nowrap")} aria-hidden={collapsed} data-testid="desktop-nav-utils">
            {/* Upgrade Button - for non-subscribers */}
            {user && stats?.subscription_tier === "free" && (
              <Link
                to="/subscription"
                className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-90 transition-opacity"
                data-testid="nav-upgrade"
              >
                <Crown className="w-3.5 h-3.5" />
                Upgrade
              </Link>
            )}

            {/* User Stats */}
            {user && stats && (
              <Badge variant="outline" className="gap-1 whitespace-nowrap" data-testid="navbar-score">
                <Trophy className="w-3 h-3 text-secondary" />
                {stats.total_score} pts
              </Badge>
            )}

            {/* Python / Blocks mode */}
            <ModeToggle compact />

            {/* Dark Mode Toggle (logged-in users find it in their account menu) */}
            {!user && (
              <div className="flex items-center gap-1.5" title="Dark mode">
                <Sun className="w-3.5 h-3.5 text-muted-foreground hidden min-[1700px]:block" />
                <Switch
                  checked={darkMode}
                  onCheckedChange={setDarkMode}
                  data-testid="theme-toggle"
                />
                <Moon className="w-3.5 h-3.5 text-muted-foreground hidden min-[1700px]:block" />
              </div>
            )}

            {/* Notification Bell - for logged in users */}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="relative"
                    data-testid="notification-bell"
                  >
                    <Bell className="w-5 h-5" />
                    {notificationCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center font-medium">
                        {notificationCount > 9 ? "9+" : notificationCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[320px]">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No new notifications
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <DropdownMenuItem
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        className="flex flex-col items-start gap-1 p-3 cursor-pointer"
                        data-testid={`notification-${notification.id}`}
                      >
                        <span className="text-sm font-medium text-foreground">
                          {notification.type === "admin_pending" && "📋 "}
                          {notification.type === "question_approved" && "✅ "}
                          {notification.type === "question_rejected" && "📝 "}
                          {notification.message}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {notification.type === "admin_pending" && "Click to review"}
                          {notification.type === "question_approved" && "Click to claim points"}
                          {notification.type === "question_rejected" && "Click to dismiss"}
                        </span>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Account menu: profile, nickname, dark mode, logout - one compact control keeps the bar from collapsing */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1.5 rounded-full border border-border pl-1.5 pr-2 py-1 hover:bg-muted transition-colors" data-testid="nav-user-menu-btn" title={user.nickname || user.name}>
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                    ) : (
                      <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center"><User className="w-3.5 h-3.5 text-primary" /></span>
                    )}
                    <span className="text-sm font-medium text-foreground max-w-[110px] truncate">{user.nickname || user.name}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" data-testid="nav-user-menu">
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2 cursor-pointer" data-testid="nav-profile-link"><User className="w-4 h-4" /> My profile</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="gap-2 cursor-pointer" onSelect={() => setShowRegenerateModal(true)} data-testid="regenerate-nickname-btn">
                    <RefreshCw className="w-4 h-4" /> Change nickname
                  </DropdownMenuItem>
                  <DropdownMenuItem className="gap-2 cursor-pointer justify-between" onSelect={(e) => { e.preventDefault(); setDarkMode(!darkMode); }} data-testid="theme-toggle-item">
                    <span className="flex items-center gap-2">{darkMode ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />} Dark mode</span>
                    <Switch checked={darkMode} onCheckedChange={setDarkMode} data-testid="theme-toggle" className="scale-90" />
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="gap-2 cursor-pointer text-destructive focus:text-destructive" onSelect={handleLogout} data-testid="logout-btn">
                    <LogOut className="w-4 h-4" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/subscription"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="plans-link"
                >
                  Plans
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogin}
                  data-testid="login-btn"
                >
                  Login
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          {collapsed && (
          <div className="flex items-center gap-2" data-testid="menu-nav-controls">
            {user && stats && (
              <Badge variant="outline" className="gap-1 text-xs">
                <Trophy className="w-3 h-3 text-secondary" />
                {stats.total_score}
              </Badge>
            )}
            <div className="flex items-center gap-1">
              <Sun className="w-3.5 h-3.5 text-muted-foreground" />
              <Switch
                checked={darkMode}
                onCheckedChange={setDarkMode}
                data-testid="theme-toggle-mobile"
              />
              <Moon className="w-3.5 h-3.5 text-muted-foreground" />
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </Button>
          </div>
          )}
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && collapsed && (
          <div className="border-t border-border py-4 space-y-3" data-testid="mobile-menu-panel">
            <div className="px-2 pb-1 flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Coding mode</span>
              <ModeToggle compact testId="mode-toggle-mobile" />
            </div>
            {user?.is_admin && (
              <Link
                to="/admin"
                className={`flex items-center gap-2 px-2 py-2 rounded-lg text-sm font-medium ${
                  location.pathname.startsWith("/admin")
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground"
                }`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-admin"
              >
                <Settings className="w-4 h-4" />
                Admin
              </Link>
            )}

            {/* Manage Students Link Mobile - School Admins Only */}
            {user?.school_admin && (
              <Link
                to="/manage-students"
                className={`flex items-center gap-2 px-2 py-2 rounded-lg text-sm font-medium ${
                  location.pathname === "/manage-students"
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground"
                }`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-manage-students"
              >
                <Users className="w-4 h-4" />
                Manage Students
              </Link>
            )}

            {/* Host Live Drill Mobile - School Admins Only */}
            {user?.school_admin && (
              <Link
                to="/live-host"
                className={`flex items-center gap-2 px-2 py-2 rounded-lg text-sm font-medium ${
                  location.pathname === "/live-host"
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground"
                }`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-host-live"
              >
                <MonitorPlay className="w-4 h-4" />
                Host Live Drill
              </Link>
            )}

            {/* Join Live Drill Mobile - Everyone */}
            <Link
              to="/join"
              className={`flex items-center gap-2 px-2 py-2 rounded-lg text-sm font-medium ${
                location.pathname === "/join"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground"
              }`}
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-join-live"
            >
              <Radio className="w-4 h-4" />
              Join Live Drill
            </Link>

            {/* Rankings Link Mobile */}
            <Link
              to="/rankings"
              className={`flex items-center gap-2 px-2 py-2 rounded-lg text-sm font-medium ${
                location.pathname === "/rankings"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground"
              }`}
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-rankings"
            >
              <Trophy className="w-4 h-4" />
              Rankings
            </Link>

            {/* Instructions Link Mobile */}
            <Link
              to="/instructions"
              className={`flex items-center gap-2 px-2 py-2 rounded-lg text-sm font-medium ${
                location.pathname === "/instructions"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground"
              }`}
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-instructions"
            >
              <BookOpen className="w-4 h-4" />
              Instructions
            </Link>

            {/* About Link Mobile */}
            <Link
              to="/about"
              className={`flex items-center gap-2 px-2 py-2 rounded-lg text-sm font-medium ${
                location.pathname === "/about"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground"
              }`}
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-about"
            >
              <Info className="w-4 h-4" />
              About
            </Link>

            {/* Contact Us Mobile */}
            <button
              onClick={() => {
                setShowContactModal(true);
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-2 px-2 py-2 rounded-lg text-sm font-medium text-muted-foreground w-full text-left"
              data-testid="mobile-nav-contact"
            >
              <Mail className="w-4 h-4" />
              Contact Us
            </button>

            {user ? (
              <div className="px-2 pt-2 border-t border-border space-y-3">
                {/* Notifications Section Mobile */}
                {notifications.length > 0 && (
                  <div className="pb-2 border-b border-border">
                    <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <Bell className="w-3 h-3" />
                      Notifications ({notificationCount})
                    </p>
                    <div className="space-y-1">
                      {notifications.slice(0, 3).map((notification) => (
                        <button
                          key={notification.id}
                          onClick={() => {
                            handleNotificationClick(notification);
                            setMobileMenuOpen(false);
                          }}
                          className="w-full text-left p-2 rounded-lg bg-muted/50 hover:bg-muted text-sm"
                          data-testid={`mobile-notification-${notification.id}`}
                        >
                          <span className="text-foreground">
                            {notification.type === "admin_pending" && "📋 "}
                            {notification.type === "question_approved" && "✅ "}
                            {notification.type === "question_rejected" && "📝 "}
                            {notification.message}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* User Info with Nickname - Link to Profile */}
                <Link
                  to="/profile"
                  className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-muted transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-profile-link"
                >
                  {user.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name} 
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {stats?.nickname || user.nickname || "Loading..."}
                    </p>
                  </div>
                </Link>

                {/* Nickname Regenerate Button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-2"
                  onClick={() => {
                    setShowRegenerateModal(true);
                    setMobileMenuOpen(false);
                  }}
                  data-testid="mobile-regenerate-nickname-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                  Change Nickname
                </Button>

                {/* Subscription Link for non-subscribers */}
                {stats?.subscription_tier === "free" && (
                  <Link
                    to="/subscription"
                    className="flex items-center justify-center gap-2 w-full text-xs font-medium px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-upgrade-btn"
                  >
                    <Crown className="w-3.5 h-3.5" />
                    Upgrade to Student Plan
                  </Link>
                )}

                {/* Create Question for all logged-in users */}
                <Link
                  to="/create-question"
                  className="flex items-center justify-center gap-2 w-full text-sm font-medium px-3 py-2 rounded-lg bg-primary/10 text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-create-question-btn"
                >
                  <PenSquare className="w-4 h-4" />
                  Create Question
                </Link>

                {/* Logout Button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  data-testid="mobile-logout-btn"
                >
                  Logout
                </Button>
              </div>
            ) : (
              <div className="px-2 pt-2 border-t border-border space-y-3">
                <Link
                  to="/subscription"
                  className="flex items-center justify-center gap-2 w-full text-sm font-medium px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-plans-link"
                >
                  <Crown className="w-4 h-4" />
                  View Plans
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    handleLogin();
                    setMobileMenuOpen(false);
                  }}
                  data-testid="mobile-login-btn"
                >
                  Login
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Regenerate Nickname Modal */}
      {showRegenerateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowRegenerateModal(false)}
          />
          <div className="relative bg-card rounded-2xl border border-border p-6 max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-2">Regenerate Nickname?</h3>
            <div className="space-y-3 text-sm text-muted-foreground mb-6">
              <p>
                Your current nickname: <span className="font-medium text-foreground">{user?.nickname || user?.name}</span>
              </p>
              {canRegenerate ? (
                <>
                  <p className="text-amber-600">
                    ⚠️ You can only regenerate your nickname <strong>once per day</strong>.
                  </p>
                  <p>
                    You cannot get your old nickname back after regenerating.
                  </p>
                </>
              ) : (
                <p className="text-red-500">
                  ❌ You've already regenerated your nickname today. Try again tomorrow!
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowRegenerateModal(false)}
              >
                Keep Current
              </Button>
              {canRegenerate && (
                <Button
                  className="flex-1 gap-2"
                  onClick={handleRegenerateNickname}
                  disabled={regenerating}
                  data-testid="confirm-regenerate-btn"
                >
                  {regenerating ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Regenerate
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />

      {/* Contact Modal */}
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />
    </nav>
  );
}
